/**
 * Blocurile care afiseaza DOAR ce a scris operatorul pietei.
 *
 * Regula lor comuna: un bloc fara continut nu se randeaza. Un titlu de sectiune
 * urmat de nimic arata a pagina rupta, iar operatorul care a adaugat blocul si
 * nu l-a completat afla asta din editor (unde blocul e vizibil si gol), nu din
 * vitrina clientului.
 */

import { useEffect, useState } from "react";

import { blockFlag, blockItems, blockNumber, blockText } from "@deliveryapp/shared";

import { IconPin } from "../components/icons";
import { ImageFallback, ResponsiveImage } from "../components/Media";
import { Modal } from "../components/Modal";
import { columnClass, paragraphs, safeImage, type BlockProps } from "./context";
import { BlockHeading, BlockLink, BlockSection } from "./parts";

const HERO_HEIGHTS: Record<string, string> = {
  compact: "min-h-[180px] sm:min-h-[220px]",
  normala: "min-h-[260px] sm:min-h-[340px]",
  inalta: "min-h-[340px] sm:min-h-[460px]",
};

function heroHeight(value: string): string {
  return HERO_HEIGHTS[value] ?? HERO_HEIGHTS.normala ?? "";
}

/**
 * Coperta.
 *
 * Imaginea e FUNDAL, cu textul peste ea. Ca imagine separata deasupra titlului,
 * impingea continutul sub marginea ecranului pe telefon: primul lucru vizibil
 * era o poza decorativa, iar motivul vizitei incepea abia dupa ea.
 */
export function HeroBlock({ block, ctx, headingLevel }: BlockProps) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  // Titlul cade pe textul pietei, nu pe un sir fix: pietele publicate inainte de
  // builder isi tin titlul copertii in `texts["home.hero_title"]`, iar acela se
  // rezolva pe limba ACTIVA a vizitatorului. Rezerva e goala, deci o coperta
  // adaugata din editor si lasata fara titlu ramane fara titlu.
  const title = blockText(block, "title", ctx.t("home.hero_title", ""));
  const subtitle = blockText(block, "subtitle");
  const candidateImage = safeImage(blockText(block, "imageUrl"));
  const image = candidateImage === failedImage ? null : candidateImage;
  const align = blockText(block, "align", "stanga");
  const overlay = Math.min(85, Math.max(0, blockNumber(block, "overlay", 55)));
  const ctaLabel = blockText(block, "ctaLabel");
  const ctaHref = blockText(block, "ctaHref");

  if (title.length === 0 && subtitle.length === 0 && image === null) return null;

  const onImage = image !== null;
  const split = blockText(block, "composition") === "split";
  const overPhoto = onImage && !split;
  return (
    <section
      // `id`-ul blocului e ancora lui: fara el, o legatura scrisa in editor ca
      // `#vitrine` nu are unde sa duca, iar butonul principal al paginii nu face
      // nimic la apasare.
      id={block.id}
      className={[
        "da-editorial-hero relative overflow-hidden rounded-card",
        split ? "da-hero-split" : "",
        // Inaltimea minima se aplica DOAR peste o imagine. Fara poza, coperta isi
        // ia inaltimea din continut, ca inainte: altfel o piata fara imagine de
        // coperta primea un dreptunghi gol de 260-340 px care impingea
        // restaurantele sub marginea telefonului.
        onImage ? heroHeight(blockText(block, "height", "normala")) : "",
        overPhoto ? "text-white" : "border border-line bg-surface text-ink",
      ].join(" ")}
    >
      {onImage ? (
        <>
          <img
            src={image}
            alt=""
            // Coperta e cel mai mare element de pe primul ecran, deci ea decide
            // LCP-ul: se incarca devreme, nu lene.
            {...{ fetchpriority: "high" }}
            onError={() => setFailedImage(image)}
            style={{ objectPosition: blockText(block, "imagePosition", "center") }}
            className={split ? "da-hero-split-photo" : "absolute inset-0 h-full w-full object-cover"}
          />
          {/*
            Contrastul textului nu poate depinde de poza pe care si-o incarca
            fiecare piata: valul intunecat il garanteaza — dar numai daca e gros
            ACOLO UNDE STA TEXTUL. Un gradient care se subtia spre varf lasa
            titlul, cel mai mare element, pe partea de 20% negru: peste o poza
            deschisa (farfurie pe alb, terasa insorita — exact ce se pune ca
            imagine de copertă) alb pe alb.
          */}
          {overPhoto ? <div aria-hidden="true" className="absolute inset-0 bg-black" style={{ opacity: overlay / 100 }} /> : null}
        </>
      ) : null}

      <div
        className={[
          "da-hero-copy relative flex h-full flex-col justify-center gap-4 p-6 sm:p-10",
          align === "centru" ? "items-center text-center" : "",
        ].join(" ")}
      >
        {blockText(block, "eyebrow") ? <p className="text-xs font-bold uppercase tracking-[0.18em]">{blockText(block, "eyebrow")}</p> : null}
        {title.length === 0 ? null : headingLevel === 1 ? (
          <h1 className="da-display-title max-w-readable text-4xl font-extrabold sm:text-6xl">{title}</h1>
        ) : (
          <h2 className="da-display-title max-w-readable text-3xl font-extrabold sm:text-5xl">{title}</h2>
        )}
        {subtitle.length === 0 ? null : (
          <p className={`max-w-readable text-base sm:text-lg ${overPhoto ? "text-white/90" : "text-ink-muted"}`}>
            {subtitle}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <BlockLink label={ctaLabel} href={ctaHref} variant={overPhoto ? "contrast" : "primary"} />
          <BlockLink label={blockText(block, "secondaryLabel")} href={blockText(block, "secondaryHref")} variant={overPhoto ? "contrast" : "secondary"} />
        </div>
      </div>
    </section>
  );
}

/**
 * Coperta cu adresa in interior — deschiderea obisnuita a unei platforme de
 * livrare. Butonul deschide ACEEASI fereastra de adresa ca bara subtire; nu
 * exista un al doilea formular care ar putea diverge de primul.
 */
export function HeroSearchBlock({ block, ctx, headingLevel }: BlockProps) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const title = blockText(block, "title", ctx.t("home.hero_title", "Ce mănânci azi?"));
  const subtitle = blockText(block, "subtitle");
  const candidateImage = safeImage(blockText(block, "imageUrl"));
  const image = candidateImage === failedImage ? null : candidateImage;
  const overlay = Math.min(85, Math.max(0, blockNumber(block, "overlay", 55)));
  const addressLabel = blockText(block, "addressLabel", ctx.t("home.address_title", "Unde livrăm?"));
  const addressCta = blockText(block, "addressCta", "Vezi restaurantele");
  const onImage = image !== null;
  const split = blockText(block, "composition") === "split";
  const overPhoto = onImage && !split;

  return (
    <section
      id={block.id}
      className={[
        "da-editorial-hero relative overflow-hidden rounded-card",
        split ? "da-hero-split" : "",
        // Fara fotografie, inaltimea fixa producea un panou gri aproape gol de
        // 340–460 px. Adresa si restaurantele cadeau sub primul ecran exact pe
        // telefon. Continutul simplu isi ia acum inaltimea naturala.
        onImage ? heroHeight(blockText(block, "height", "inalta")) : "",
        overPhoto ? "text-white" : "border border-line bg-surface text-ink",
      ].join(" ")}
    >
      {onImage ? (
        <>
          <img src={image} alt="" {...{ fetchpriority: "high" }} onError={() => setFailedImage(image)} style={{ objectPosition: blockText(block, "imagePosition", "center") }} className={split ? "da-hero-split-photo" : "absolute inset-0 h-full w-full object-cover"} />
          {overPhoto ? <div aria-hidden="true" className="absolute inset-0 bg-black" style={{ opacity: overlay / 100 }} /> : null}
        </>
      ) : null}

      <div className="da-hero-copy relative flex h-full flex-col justify-center gap-4 p-6 sm:p-10">
        {blockText(block, "eyebrow") ? <p className="text-xs font-bold uppercase tracking-[0.18em]">{blockText(block, "eyebrow")}</p> : null}
        {headingLevel === 1 ? (
          <h1 className={`da-display-title max-w-readable text-4xl font-extrabold sm:text-6xl ${overPhoto ? "da-hero-text" : ""}`}>{title}</h1>
        ) : (
          <h2 className={`da-display-title max-w-readable text-3xl font-extrabold sm:text-5xl ${overPhoto ? "da-hero-text" : ""}`}>{title}</h2>
        )}
        {subtitle.length === 0 ? null : (
          <p className={`max-w-readable text-base sm:text-lg ${overPhoto ? "da-hero-text text-white/90" : "text-ink-muted"}`}>
            {subtitle}
          </p>
        )}

        {/* Cardul de adresa sta pe fundal opac chiar si peste poza: pe o copertă
            deschisă, un card semitransparent ar fi facut textul din el ilizibil
            exact acolo unde clientul trebuie sa citeasca ce a introdus. Tot
            cardul e apasabil — nu doar butonul — ca pe telefon sa nu trebuiasca
            nimerit un buton mic in coltul copertei. */}
        <button
          type="button"
          onClick={ctx.requestAddress}
          className="da-press flex w-full max-w-xl flex-col gap-2 rounded-card border border-line bg-canvas p-2.5 text-left text-ink shadow-lg sm:flex-row sm:items-center sm:gap-3 sm:pl-3.5"
          aria-label={ctx.addressLabel === null ? addressCta : "Schimbă adresa de livrare"}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            <IconPin size={22} className="shrink-0 text-brand-on-surface" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-muted">
                {ctx.addressLabel === null ? addressLabel : "Livrăm la"}
              </span>
              <span className="da-clamp-1 block text-base font-bold text-ink">
                {ctx.addressLabel ?? "Scrie strada sau folosește locația"}
              </span>
            </span>
          </span>
          {/* Pe telefon butonul coboara pe randul lui, pe toata latimea: langa
              text, impingea eticheta pe patru randuri. */}
          <span className="block w-full shrink-0 rounded-control bg-brand px-4 py-2.5 text-center text-sm font-bold text-brand-contrast sm:w-auto sm:text-base">
            {ctx.addressLabel === null ? addressCta : "Schimbă"}
          </span>
        </button>
        <div><BlockLink label={blockText(block, "ctaLabel")} href={blockText(block, "ctaHref")} variant={overPhoto ? "contrast" : "secondary"} /></div>
      </div>
    </section>
  );
}

export function TextBlock({ block, headingLevel }: BlockProps) {
  const body = blockText(block, "body");
  const title = blockText(block, "title");
  if (body.length === 0 && title.length === 0) return null;
  const align = blockText(block, "align", "stanga");
  const narrow = blockFlag(block, "narrow", true);

  return (
    <BlockSection id={block.id} className={align === "centru" ? "items-center text-center" : ""}>
      <BlockHeading title={title} subtitle={blockText(block, "subtitle")} level={headingLevel} align={align} />
      <div className={`flex flex-col gap-3 text-base text-ink ${narrow ? "max-w-readable" : ""}`}>
        {paragraphs(body).map((part, index) => (
          <p key={index}>{part}</p>
        ))}
      </div>
    </BlockSection>
  );
}

export function ImageTextBlock({ block, headingLevel }: BlockProps) {
  const title = blockText(block, "title");
  const body = blockText(block, "body");
  const image = safeImage(blockText(block, "imageUrl"));
  if (title.length === 0 && body.length === 0 && image === null) return null;
  const imageFirst = blockText(block, "imageSide", "stanga") !== "dreapta";

  const picture = (
    <ResponsiveImage
      src={image}
      alt=""
      ratio="4/3"
      position={blockText(block, "imagePosition", "center")}
      sizes="(min-width: 768px) 45vw, 100vw"
      fallback={<ImageFallback label="📷" />}
    />
  );

  const words = (
    <div className="flex flex-col gap-3">
      <BlockHeading title={title} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      {paragraphs(body).map((part, index) => (
        <p key={index} className="text-base text-ink">
          {part}
        </p>
      ))}
      <div>
        <BlockLink label={blockText(block, "ctaLabel")} href={blockText(block, "ctaHref")} variant="secondary" />
      </div>
    </div>
  );

  return (
    <section id={block.id} className="da-story-section grid items-center gap-6 md:grid-cols-2 md:gap-12">
      {imageFirst ? picture : words}
      {imageFirst ? words : picture}
    </section>
  );
}

export function GalleryBlock({ block, headingLevel }: BlockProps) {
  const [active, setActive] = useState<number | null>(null);
  const items = blockItems(block).filter((item) => safeImage(item.imageUrl ?? "") !== null);
  useEffect(() => {
    if (active === null) return;
    const navigate = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      setActive(index => index === null || items.length === 0 ? null : (index + (event.key === "ArrowRight" ? 1 : -1) + items.length) % items.length);
    };
    document.addEventListener("keydown", navigate);
    return () => document.removeEventListener("keydown", navigate);
  }, [active, items.length]);
  if (items.length === 0) return null;
  const selected = active === null ? null : items[active];

  return (
    <BlockSection id={block.id}>
      <BlockHeading title={blockText(block, "title")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className={blockText(block, "layout") === "editorial" ? "da-gallery-editorial grid grid-cols-2 gap-3" : "grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}>
        {items.map((item, index) => (
          <li key={index} className="flex flex-col gap-1.5">
            <button type="button" onClick={() => setActive(index)} aria-label={`Mărește fotografia ${index + 1}${item.caption ? `: ${item.caption}` : ""}`} className="group rounded-card text-left outline-offset-4">
            <ResponsiveImage
              src={safeImage(item.imageUrl ?? "")}
              alt={item.caption ?? ""}
              ratio="1/1"
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
              fallback={<ImageFallback label="📷" />}
              className="transition-transform group-hover:scale-[1.015]"
            />
            </button>
            {item.caption === undefined || item.caption.length === 0 ? null : (
              <span className="text-sm text-ink-muted">{item.caption}</span>
            )}
          </li>
        ))}
      </ul>
      {selected && <Modal open onClose={() => setActive(null)} title={blockText(block, "title", "Galerie foto")} size="lg">
        <img src={safeImage(selected.imageUrl ?? "") ?? undefined} alt={selected.caption || `Fotografia ${(active ?? 0) + 1}`} className="max-h-[65dvh] w-full rounded-control object-contain" />
        <div className="mt-4 flex items-center justify-between gap-3" aria-live="polite"><button type="button" disabled={items.length < 2} aria-label="Fotografia anterioară" onClick={() => setActive(index => ((index ?? 0) - 1 + items.length) % items.length)} className="da-tap rounded-control border border-line px-4 text-xl disabled:opacity-25">←</button><div className="text-center"><p className="text-sm">{selected.caption}</p><p className="mt-1 text-xs text-ink-muted">{(active ?? 0) + 1} / {items.length}</p></div><button type="button" disabled={items.length < 2} aria-label="Fotografia următoare" onClick={() => setActive(index => ((index ?? 0) + 1) % items.length)} className="da-tap rounded-control border border-line px-4 text-xl disabled:opacity-25">→</button></div>
      </Modal>}
    </BlockSection>
  );
}

export function StatsBlock({ block, headingLevel }: BlockProps) {
  const items = blockItems(block).filter((item) => (item.value ?? "").trim().length > 0);
  if (items.length === 0) return null;

  return (
    <BlockSection id={block.id}>
      <BlockHeading title={blockText(block, "title")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <dl className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((item, index) => (
          <div key={index} className="da-card flex flex-col gap-1 p-4">
            <dt className="order-2 text-sm text-ink-muted">{item.label ?? ""}</dt>
            <dd className="order-1 text-3xl font-bold text-brand">{item.value}</dd>
          </div>
        ))}
      </dl>
    </BlockSection>
  );
}

export function TestimonialsBlock({ block, headingLevel }: BlockProps) {
  const items = blockItems(block).filter((item) => (item.quote ?? "").trim().length > 0);
  if (items.length === 0) return null;

  return (
    <BlockSection id={block.id}>
      <BlockHeading title={blockText(block, "title")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className={`grid gap-4 ${columnClass(blockText(block, "columns", "3"))}`}>
        {items.map((item, index) => (
          <li key={index} className="da-card flex flex-col gap-3 p-5">
            <blockquote className="text-base text-ink">{`„${item.quote}"`}</blockquote>
            {(item.author ?? "").length === 0 ? null : (
              <p className="text-sm text-ink-muted">
                <span className="font-semibold text-ink">{item.author}</span>
                {(item.role ?? "").length === 0 ? "" : ` · ${item.role}`}
              </p>
            )}
          </li>
        ))}
      </ul>
    </BlockSection>
  );
}

export function StepsBlock({ block, ctx, headingLevel }: BlockProps) {
  const items = blockItems(block).filter((item) => (item.title ?? "").trim().length > 0);
  if (items.length === 0) return null;

  return (
    <BlockSection id={block.id}>
      {/* Ca la copertă: titlul sectiunii se putea suprascrie din `texts` pe
          pietele vechi, iar acela se rezolva pe limba activa. */}
      <BlockHeading
        title={blockText(block, "title", ctx.t("home.info_title", "Cum funcționează"))}
        subtitle={blockText(block, "subtitle")}
        level={headingLevel}
      />
      <ol className={`grid gap-3 ${columnClass(String(Math.min(4, Math.max(2, items.length))))}`}>
        {items.map((item, index) => (
          <li key={index} className="da-card flex flex-col gap-1 p-4 text-sm text-ink">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand">{`Pasul ${index + 1}`}</span>
            <span className="text-base font-semibold text-ink">{item.title}</span>
            {(item.body ?? "").length === 0 ? null : <span className="text-ink-muted">{item.body}</span>}
          </li>
        ))}
      </ol>
    </BlockSection>
  );
}

export function FeaturesBlock({ block, headingLevel }: BlockProps) {
  const items = blockItems(block).filter(
    (item) => (item.title ?? "").trim().length > 0 || (item.body ?? "").trim().length > 0,
  );
  if (items.length === 0) return null;

  return (
    <BlockSection id={block.id}>
      <BlockHeading title={blockText(block, "title")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className={`grid gap-4 ${columnClass(blockText(block, "columns", "3"))}`}>
        {items.map((item, index) => (
          <li key={index} className="da-card flex flex-col gap-2 p-5">
            {(item.icon ?? "").length === 0 ? null : (
              <span aria-hidden="true" className="text-3xl leading-none">
                {item.icon}
              </span>
            )}
            {(item.title ?? "").length === 0 ? null : (
              <span className="text-base font-semibold text-ink">{item.title}</span>
            )}
            {(item.body ?? "").length === 0 ? null : <span className="text-sm text-ink-muted">{item.body}</span>}
          </li>
        ))}
      </ul>
    </BlockSection>
  );
}

/**
 * Intrebari si raspunsuri. `<details>`, nu o stare in React: functioneaza si
 * inainte ca JavaScript-ul sa se incarce, iar cautarea din browser gaseste text
 * si in raspunsurile inchise.
 */
export function FaqBlock({ block, headingLevel }: BlockProps) {
  const items = blockItems(block).filter((item) => (item.question ?? "").trim().length > 0);
  if (items.length === 0) return null;

  return (
    <BlockSection id={block.id}>
      <BlockHeading title={blockText(block, "title")} subtitle={blockText(block, "subtitle")} level={headingLevel} />
      <ul className="flex max-w-readable flex-col gap-2">
        {items.map((item, index) => (
          <li key={index} className="da-card overflow-hidden">
            <details>
              <summary className="da-tap flex cursor-pointer items-center px-4 py-3 text-base font-semibold text-ink">
                {item.question}
              </summary>
              {(item.answer ?? "").length === 0 ? null : (
                <div className="flex flex-col gap-2 px-4 pb-4 text-sm text-ink-muted">
                  {paragraphs(item.answer ?? "").map((part, partIndex) => (
                    <p key={partIndex}>{part}</p>
                  ))}
                </div>
              )}
            </details>
          </li>
        ))}
      </ul>
    </BlockSection>
  );
}

const TONE_CLASSES: Record<string, string> = {
  brand: "bg-brand text-brand-contrast",
  surface: "da-card text-ink",
  contur: "border border-line text-ink",
};

function toneClass(value: string): string {
  return TONE_CLASSES[value] ?? TONE_CLASSES.brand ?? "";
}

export function CtaBannerBlock({ block, headingLevel }: BlockProps) {
  const title = blockText(block, "title");
  const body = blockText(block, "body");
  if (title.length === 0 && body.length === 0) return null;
  const tone = blockText(block, "tone", "brand");
  const onBrand = tone === "brand";

  return (
    <section id={block.id} className={`da-editorial-cta flex flex-col items-start gap-4 rounded-card p-7 sm:p-12 ${toneClass(tone)}`}>
      {title.length === 0 ? null : headingLevel === 1 ? (
        <h1 className="text-2xl font-bold sm:text-3xl">{title}</h1>
      ) : (
        <h2 className="text-xl font-bold sm:text-2xl">{title}</h2>
      )}
      {body.length === 0 ? null : <p className={`max-w-readable ${onBrand ? "opacity-90" : "text-ink-muted"}`}>{body}</p>}
      <BlockLink
        label={blockText(block, "ctaLabel")}
        href={blockText(block, "ctaHref")}
        variant={onBrand ? "contrast" : "primary"}
      />
    </section>
  );
}

/**
 * Banda de anunt. Se poate inchide, si alegerea tine cat tine pagina deschisa:
 * fara buton de inchidere, un anunt lung ar fi stat permanent peste continut pe
 * telefon. Nu se retine intre vizite — un anunt care dispare definitiv dupa o
 * apasare gresita e mai rau decat unul care revine.
 */
export function PromoStripBlock({ block }: BlockProps) {
  const [closed, setClosed] = useState(false);
  const message = blockText(block, "message");
  if (message.length === 0 || closed) return null;
  const tone = blockText(block, "tone", "brand");

  return (
    <div id={block.id} className={`da-block-anchor flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card px-4 py-3 text-sm ${toneClass(tone)}`}>
      <span className="flex-1 font-medium">{message}</span>
      <BlockLink
        label={blockText(block, "ctaLabel")}
        href={blockText(block, "ctaHref")}
        variant={tone === "brand" ? "contrast" : "secondary"}
      />
      <button
        type="button"
        onClick={() => setClosed(true)}
        aria-label="Închide anunțul"
        className="da-tap rounded-control px-2 text-lg leading-none opacity-70 hover:opacity-100"
      >
        ×
      </button>
    </div>
  );
}

export function LegalNoteBlock({ block }: BlockProps) {
  const body = blockText(block, "body");
  if (body.length === 0) return null;
  return (
    <div id={block.id} className="da-block-anchor flex max-w-readable flex-col gap-2 text-xs text-ink-muted">
      {paragraphs(body).map((part, index) => (
        <p key={index}>{part}</p>
      ))}
    </div>
  );
}

const DIVIDER_SIZES: Record<string, string> = { mic: "h-2", normal: "h-6", mare: "h-12" };

export function DividerBlock({ block }: BlockProps) {
  const size = DIVIDER_SIZES[blockText(block, "size", "normal")] ?? DIVIDER_SIZES.normal;
  if (blockFlag(block, "line", false)) {
    return <hr id={block.id} className="da-block-anchor border-line" aria-hidden="true" />;
  }
  return <div id={block.id} className={`da-block-anchor ${size}`} aria-hidden="true" />;
}
